/**
 * Rapid X - Admin Feed Management Page
 * P0 Super Admin Data Cleanup / Feed Management
 * 
 * Allows Super Admin to:
 * - View feed statistics
 * - Clear old incidents, attendance, notifications, patrol logs
 * - Archive vs hard delete options
 * - Bulk operations with confirmations
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Trash2, Archive, Shield, AlertTriangle, 
  Clock, Bell, Route, FileText,
  RefreshCw, Loader2, Database, TrendingDown,
  CheckCircle2, XCircle, Calendar, Activity
} from 'lucide-react';

export default function FeedManagementPage() {
  const { api, user, isAdmin } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Clear options
  const [clearOptions, setClearOptions] = useState({
    olderThanDays: 90,
    archive: true,
    statusFilter: 'all'
  });

  // Check admin access
  const isSuperAdmin = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => {
    if (isSuperAdmin) {
      loadStats();
    }
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/feeds/stats');
      setStats(response.data?.stats || {});
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load feed statistics');
    } finally {
      setLoading(false);
    }
  };

  const confirmClearAction = (action) => {
    setConfirmAction(action);
    setShowConfirmDialog(true);
  };

  const executeClearAction = async () => {
    if (!confirmAction) return;
    
    setActionLoading(true);
    try {
      let response;
      
      switch (confirmAction.type) {
        case 'incidents':
          response = await api.post('/feeds/incidents/clear', {
            older_than_days: clearOptions.olderThanDays,
            status_filter: clearOptions.statusFilter === 'all' ? null : clearOptions.statusFilter,
            archive: clearOptions.archive
          });
          break;
          
        case 'attendance':
          response = await api.post('/feeds/attendance/clear', {
            older_than_days: clearOptions.olderThanDays,
            archive: clearOptions.archive
          });
          break;
          
        case 'notifications':
          response = await api.post(`/feeds/notifications/clear?older_than_days=${clearOptions.olderThanDays}&read_only=true`);
          break;
          
        case 'patrol-logs':
          response = await api.post(`/feeds/patrol-logs/clear?older_than_days=${clearOptions.olderThanDays}&completed_only=true&archive=${clearOptions.archive}`);
          break;
          
        case 'activity':
          response = await api.post(`/feeds/activity/clear?older_than_days=${clearOptions.olderThanDays}`);
          break;
          
        default:
          throw new Error('Unknown action type');
      }
      
      toast.success(response.data?.message || `${confirmAction.label} cleared successfully`);
      setShowConfirmDialog(false);
      setConfirmAction(null);
      loadStats();
      
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute action');
    } finally {
      setActionLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-zinc-400">Only Super Admins can access feed management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feed-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            FEED MANAGEMENT
          </h1>
          <p className="text-sm text-zinc-500">Admin data cleanup and archival controls</p>
        </div>
        
        <Button
          variant="outline"
          onClick={loadStats}
          disabled={loading}
          className="border-zinc-700"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Stats
        </Button>
      </div>

      {/* Warning Banner */}
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium">Destructive Operations</p>
              <p className="text-amber-300/80 text-sm mt-1">
                These actions modify or remove data from the system. Archived items can be recovered, 
                but deleted items are permanently removed. All actions are logged for audit purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clear Options Card */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white font-rajdhani flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-400" />
            Cleanup Settings
          </CardTitle>
          <CardDescription className="text-zinc-500">
            Configure the parameters for cleanup operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Records Older Than</Label>
              <Select 
                value={String(clearOptions.olderThanDays)}
                onValueChange={(v) => setClearOptions({...clearOptions, olderThanDays: parseInt(v)})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Incident Status Filter</Label>
              <Select 
                value={clearOptions.statusFilter}
                onValueChange={(v) => setClearOptions({...clearOptions, statusFilter: v})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="closed">Closed Only</SelectItem>
                  <SelectItem value="resolved">Resolved Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Operation Mode</Label>
              <div className="flex items-center gap-3 h-10">
                <Switch
                  id="archive-mode"
                  checked={clearOptions.archive}
                  onCheckedChange={(v) => setClearOptions({...clearOptions, archive: v})}
                  data-testid="archive-mode-switch"
                />
                <Label htmlFor="archive-mode" className="text-zinc-400 cursor-pointer">
                  {clearOptions.archive ? (
                    <span className="flex items-center gap-1 text-blue-400">
                      <Archive className="w-4 h-4" />
                      Archive (Recoverable)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-400">
                      <Trash2 className="w-4 h-4" />
                      Delete (Permanent)
                    </span>
                  )}
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        {/* Incidents Card */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              Incidents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Total</p>
                <p className="text-white font-bold text-lg">{stats?.incidents?.total || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Archived</p>
                <p className="text-white font-bold text-lg">{stats?.incidents?.archived || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">&gt;30 days</p>
                <p className="text-amber-400 font-bold text-lg">{stats?.incidents?.older_than_30_days || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">&gt;90 days</p>
                <p className="text-red-400 font-bold text-lg">{stats?.incidents?.older_than_90_days || 0}</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
              onClick={() => confirmClearAction({ type: 'incidents', label: 'Incidents' })}
              data-testid="clear-incidents-btn"
            >
              {clearOptions.archive ? <Archive className="w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear Old Incidents
            </Button>
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-400" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Total</p>
                <p className="text-white font-bold text-lg">{stats?.notifications?.total || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Read</p>
                <p className="text-emerald-400 font-bold text-lg">{stats?.notifications?.read || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded col-span-2">
                <p className="text-zinc-500">&gt;30 days old</p>
                <p className="text-amber-400 font-bold text-lg">{stats?.notifications?.older_than_30_days || 0}</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              onClick={() => confirmClearAction({ type: 'notifications', label: 'Read Notifications' })}
              data-testid="clear-notifications-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Read Notifications
            </Button>
          </CardContent>
        </Card>

        {/* Patrol Logs Card */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Route className="w-5 h-5 text-emerald-400" />
              Patrol Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Total</p>
                <p className="text-white font-bold text-lg">{stats?.patrol_runs?.total || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded">
                <p className="text-zinc-500">Completed</p>
                <p className="text-emerald-400 font-bold text-lg">{stats?.patrol_runs?.completed || 0}</p>
              </div>
              <div className="p-2 bg-[#09090b] rounded col-span-2">
                <p className="text-zinc-500">&gt;90 days old</p>
                <p className="text-red-400 font-bold text-lg">{stats?.patrol_runs?.older_than_90_days || 0}</p>
              </div>
            </div>
            
            <Button
              variant="outline"
              className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
              onClick={() => confirmClearAction({ type: 'patrol-logs', label: 'Completed Patrol Logs' })}
              data-testid="clear-patrols-btn"
            >
              {clearOptions.archive ? <Archive className="w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear Old Patrol Logs
            </Button>
          </CardContent>
        </Card>

        {/* Attendance Card */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Attendance Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {stats?.attendance_logs && (
                <>
                  <div className="p-2 bg-[#09090b] rounded">
                    <p className="text-zinc-500">Total</p>
                    <p className="text-white font-bold text-lg">{stats.attendance_logs.total || 0}</p>
                  </div>
                  <div className="p-2 bg-[#09090b] rounded">
                    <p className="text-zinc-500">&gt;30 days</p>
                    <p className="text-amber-400 font-bold text-lg">{stats.attendance_logs.older_than_30_days || 0}</p>
                  </div>
                </>
              )}
              {stats?.shift_check_ins && (
                <>
                  <div className="p-2 bg-[#09090b] rounded">
                    <p className="text-zinc-500">Check-ins</p>
                    <p className="text-white font-bold text-lg">{stats.shift_check_ins.total || 0}</p>
                  </div>
                  <div className="p-2 bg-[#09090b] rounded">
                    <p className="text-zinc-500">&gt;30 days</p>
                    <p className="text-amber-400 font-bold text-lg">{stats.shift_check_ins.older_than_30_days || 0}</p>
                  </div>
                </>
              )}
              {!stats?.attendance_logs && !stats?.shift_check_ins && (
                <div className="p-2 bg-[#09090b] rounded col-span-2">
                  <p className="text-zinc-500 text-center">No attendance data</p>
                </div>
              )}
            </div>
            
            <Button
              variant="outline"
              className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              onClick={() => confirmClearAction({ type: 'attendance', label: 'Attendance Logs' })}
              data-testid="clear-attendance-btn"
            >
              {clearOptions.archive ? <Archive className="w-4 h-4 mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear Old Attendance
            </Button>
          </CardContent>
        </Card>

        {/* Activity Logs Card */}
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              Activity Logs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-[#09090b] rounded">
              <p className="text-zinc-400 text-sm">
                Activity logs are hidden from display but retained for compliance. 
                This action marks old entries as hidden.
              </p>
            </div>
            
            <Button
              variant="outline"
              className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              onClick={() => confirmClearAction({ type: 'activity', label: 'Activity Logs' })}
              data-testid="clear-activity-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Hide Old Activity
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Confirm Action
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This action will affect records older than {clearOptions.olderThanDays} days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46] space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-400">Action:</span>
                <span className="text-white font-medium">
                  {clearOptions.archive ? 'Archive' : 'Delete'} {confirmAction?.label}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Records Older Than:</span>
                <span className="text-white">{clearOptions.olderThanDays} days</span>
              </div>
              {confirmAction?.type === 'incidents' && clearOptions.statusFilter !== 'all' && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Status Filter:</span>
                  <span className="text-white capitalize">{clearOptions.statusFilter}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-400">Mode:</span>
                <Badge className={clearOptions.archive 
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                  : "bg-red-500/20 text-red-400 border border-red-500/30"
                }>
                  {clearOptions.archive ? 'Archive (Recoverable)' : 'Delete (Permanent)'}
                </Badge>
              </div>
            </div>
            
            {!clearOptions.archive && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Warning: Deleted records cannot be recovered!
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
              className="border-zinc-600"
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={executeClearAction}
              className={clearOptions.archive 
                ? "bg-blue-600 hover:bg-blue-700" 
                : "bg-red-600 hover:bg-red-700"
              }
              disabled={actionLoading}
              data-testid="confirm-clear-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : clearOptions.archive ? (
                <Archive className="w-4 h-4 mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {clearOptions.archive ? 'Archive Records' : 'Delete Records'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
